import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Sparkles, Loader2, ChevronRight, X, Film, Layers } from "lucide-react";
import { api } from "../lib/api";

/**
 * CrossCreatePrompt — proposes the complementary content type after a
 * reel/carousel has been generated (or while the user is viewing the detail
 * page). All API calls are made inline here so neither the caller page nor
 * the user has to navigate to the wizard for a "I already typed this once"
 * 5-second tweak.
 *
 *  props:
 *    source       — reel or carousel doc (anything that has business_name,
 *                   category, brand_id, topic, title)
 *    targetType   — "carousel" | "reel"
 *    mode         — "modal" (overlay) | "card" (inline, no overlay)
 *    open         — only meaningful when mode === "modal"
 *    onClose      — close handler
 */
export default function CrossCreatePrompt({ source, targetType, mode = "card", open = true, onClose }) {
  const navigate = useNavigate();
  const isReel = targetType === "reel";

  // Seed topic from whatever the source has. Reels store `topic` (since the
  // recent refactor) and fall back to `title` for older docs. Carousels
  // already store `topic`.
  const seedTopic = (source?.topic && source.topic.trim()) || (source?.title || "").trim();
  const [topic, setTopic] = useState(seedTopic);
  const [template, setTemplate] = useState("educational"); // carousel only
  const [numSlides, setNumSlides] = useState(6);            // carousel only
  const [numScenes, setNumScenes] = useState(4);            // reel only
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState(0);              // 0-100 fake

  if (mode === "modal" && !open) return null;

  const closeIfIdle = () => {
    if (busy) return;
    onClose?.();
  };

  const submit = async () => {
    const trimmed = (topic || "").trim();
    if (!trimmed) {
      toast.error("Inserisci un argomento per la generazione");
      return;
    }
    setBusy(true);
    setProgress(8);
    try {
      // Build payload. When the source has a brand_id, re-use it (so brand
      // logo + colors carry over). Otherwise we have to provide a minimal
      // business block + category.
      const hasBrand = !!source?.brand_id;
      const business = {
        business_name: source?.business_name || "",
        description: source?.description || source?.title || trimmed,
        offer: "",
        target_audience: "",
        tone: "energetic",
        language: "it",
        topic: trimmed,
      };

      if (isReel) {
        // ---- create REEL ----
        const payload = hasBrand
          ? {
              category: source.category,
              brand_id: source.brand_id,
              topic: trimmed,
              business,
              num_scenes: numScenes,
              use_brand_hero: false,
              custom_images: [],
              custom_image_labels: [],
              custom_videos: [],
            }
          : {
              category: source.category,
              business,
              topic: trimmed,
              num_scenes: numScenes,
              use_brand_hero: false,
              custom_images: [],
              custom_image_labels: [],
              custom_videos: [],
            };
        const { data: job } = await api.post("/reels/generate", payload);
        const jobId = job.job_id;
        const pollStart = Date.now();
        const POLL_BUDGET = 5 * 60 * 1000;
        let resultId = null;
        let p = 12;
        while (Date.now() - pollStart < POLL_BUDGET) {
          await new Promise((r) => setTimeout(r, 4000));
          p = Math.min(p + 6, 92);
          setProgress(p);
          try {
            const { data: st } = await api.get(`/reels/jobs/${jobId}`);
            if (st.status === "done" && st.reel_id) {
              resultId = st.reel_id;
              break;
            }
            if (st.status === "failed") {
              throw new Error(st.error || "Generazione fallita");
            }
          } catch (_e) { /* tolerate transient gateway errors */ }
        }
        if (!resultId) throw new Error("Timeout — controlla la dashboard");
        setProgress(100);
        toast.success("Reel generato!");
        // Link the new reel to the source carousel as one campaign.
        if (source?.id) {
          try {
            await api.post("/campaigns/link", { reel_id: resultId, carousel_id: source.id });
          } catch (_e) { /* non-fatal — content still exists */ }
        }
        navigate(`/reel/${resultId}`);
      } else {
        // ---- create CAROUSEL ----
        // In this branch the source is always a reel (CrossCreatePrompt is
        // mounted with targetType="carousel" only from ReelDetail and from
        // the ReelCreator result step). Passing `source_reel_id` lets the
        // backend mirror the reel's images on each slide + auto-position
        // the text card.
        const sourceReelId = source?.id || null;
        const basePayload = {
          topic: trimmed,
          num_slides: numSlides,
          template,
          ...(sourceReelId ? { source_reel_id: sourceReelId } : {}),
        };
        const payload = hasBrand
          ? { ...basePayload, brand_id: source.brand_id, offer: "" }
          : { ...basePayload, category: source.category, business };
        const { data: job } = await api.post("/carousels/generate-jobs", payload);
        const jobId = job.job_id;
        const pollStart = Date.now();
        const POLL_BUDGET = 6 * 60 * 1000;
        let resultId = null;
        let p = 12;
        while (Date.now() - pollStart < POLL_BUDGET) {
          await new Promise((r) => setTimeout(r, 3000));
          p = Math.min(p + 7, 92);
          setProgress(p);
          try {
            const { data: st } = await api.get(`/carousels/jobs/${jobId}`);
            if (st.status === "done") {
              resultId = st.carousel_id;
              break;
            }
            if (st.status === "error") {
              throw new Error(st.error || "Errore generazione");
            }
          } catch (_e) { /* tolerate */ }
        }
        if (!resultId) throw new Error("Timeout — controlla la dashboard");
        setProgress(100);
        toast.success("Carosello generato!");
        // Link the new carousel to the source reel as one campaign.
        if (source?.id) {
          try {
            await api.post("/campaigns/link", { reel_id: source.id, carousel_id: resultId });
          } catch (_e) { /* non-fatal */ }
        }
        navigate(`/carousel/${resultId}`);
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || err?.message || "Errore");
      setBusy(false);
      setProgress(0);
    }
  };

  const Icon = isReel ? Film : Layers;
  const labelTarget = isReel ? "reel" : "carosello";
  const labelTargetCap = isReel ? "Reel" : "Carosello";

  const body = (
    <div className={
      mode === "modal"
        ? "bg-white text-zinc-900 rounded-xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto"
        : "bg-gradient-to-br from-violet-50 to-orange-50 border border-violet-200 rounded-xl shadow-sm"
    }>
      <div className="p-6 md:p-8">
        <div className="flex items-start justify-between gap-3 mb-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-md bg-gradient-to-br from-[#FF4400] to-violet-500 flex items-center justify-center shadow-sm">
              <Icon className="w-5 h-5 text-white" />
            </div>
            <div>
              <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-violet-600">
                · Cross-format
              </div>
              <h3 className="font-display font-black text-xl tracking-tight text-zinc-900" data-testid="cross-create-title">
                Vuoi creare anche un {labelTarget}?
              </h3>
            </div>
          </div>
          {mode === "modal" && (
            <button
              onClick={closeIfIdle}
              disabled={busy}
              data-testid="cross-create-close"
              className="text-zinc-400 hover:text-zinc-700 disabled:opacity-50"
              aria-label="Chiudi"
            >
              <X className="w-5 h-5" />
            </button>
          )}
        </div>

        <p className="text-sm text-zinc-600 mb-5">
          Riutilizza la stessa idea e business per generare un <strong>{labelTarget}</strong> abbinato.
          Modifica il topic e i parametri qui sotto prima di lanciare la generazione.
        </p>

        <div className="space-y-4">
          <div>
            <label className="block font-mono text-[10px] uppercase tracking-widest text-zinc-500 mb-1.5">Topic</label>
            <input
              type="text"
              value={topic}
              onChange={(e) => setTopic(e.target.value)}
              data-testid="cross-create-topic-input"
              disabled={busy}
              placeholder="Es. Lancio nuovo menu primavera"
              className="w-full px-3 py-2.5 bg-white border border-zinc-300 text-zinc-900 placeholder-zinc-400 focus:outline-none focus:ring-2 focus:ring-violet-400 focus:border-violet-400 rounded-md font-body disabled:bg-zinc-100"
            />
          </div>

          {isReel ? (
            <div>
              <label className="block font-mono text-[10px] uppercase tracking-widest text-zinc-500 mb-1.5">
                Numero di scene
              </label>
              <div className="grid grid-cols-4 gap-2">
                {[3, 4, 5, 6, 7, 8, 9, 10].map((n) => (
                  <button
                    key={n}
                    onClick={() => setNumScenes(n)}
                    disabled={busy}
                    data-testid={`cross-create-num-scenes-${n}`}
                    className={`px-2 py-2 rounded-md font-mono text-sm border transition-colors ${
                      numScenes === n
                        ? "bg-violet-600 border-violet-600 text-white"
                        : "bg-white border-zinc-300 text-zinc-700 hover:border-violet-300"
                    } disabled:opacity-50`}
                  >
                    {n}
                  </button>
                ))}
              </div>
            </div>
          ) : (
            <>
              <div>
                <label className="block font-mono text-[10px] uppercase tracking-widest text-zinc-500 mb-1.5">
                  Template
                </label>
                <div className="grid grid-cols-3 gap-2">
                  {[
                    { id: "educational", label: "Educativo" },
                    { id: "storytelling", label: "Storytelling" },
                    { id: "tips_list", label: "Lista tips" },
                  ].map((t) => (
                    <button
                      key={t.id}
                      onClick={() => setTemplate(t.id)}
                      disabled={busy}
                      data-testid={`cross-create-template-${t.id}`}
                      className={`px-3 py-2.5 rounded-md font-mono text-xs border transition-colors ${
                        template === t.id
                          ? "bg-violet-600 border-violet-600 text-white"
                          : "bg-white border-zinc-300 text-zinc-700 hover:border-violet-300"
                      } disabled:opacity-50`}
                    >
                      {t.label}
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <label className="block font-mono text-[10px] uppercase tracking-widest text-zinc-500 mb-1.5">
                  Numero di slide
                </label>
                <div className="flex gap-2">
                  {[4, 6, 8, 10].map((n) => (
                    <button
                      key={n}
                      onClick={() => setNumSlides(n)}
                      disabled={busy}
                      data-testid={`cross-create-num-slides-${n}`}
                      className={`flex-1 px-3 py-2 rounded-md font-mono text-sm border transition-colors ${
                        numSlides === n
                          ? "bg-violet-600 border-violet-600 text-white"
                          : "bg-white border-zinc-300 text-zinc-700 hover:border-violet-300"
                      } disabled:opacity-50`}
                    >
                      {n}
                    </button>
                  ))}
                </div>
              </div>
            </>
          )}
        </div>

        {busy && (
          <div className="mt-5">
            <div className="h-2 bg-zinc-200 rounded-full overflow-hidden">
              <div
                className="h-full bg-gradient-to-r from-violet-500 to-[#FF4400] transition-all duration-500"
                style={{ width: `${progress}%` }}
              />
            </div>
            <p className="mt-2 text-xs font-mono text-zinc-500">
              Generazione in corso… {progress}%
            </p>
          </div>
        )}

        <div className="flex gap-3 mt-7">
          <button
            onClick={submit}
            disabled={busy}
            data-testid="cross-create-submit"
            className="flex-1 inline-flex items-center justify-center gap-2 px-5 py-3 bg-zinc-900 hover:bg-zinc-800 text-white font-display font-bold rounded-md transition-colors disabled:opacity-50"
          >
            {busy ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                Generazione…
              </>
            ) : (
              <>
                <Sparkles className="w-4 h-4" />
                Genera {labelTargetCap}
                <ChevronRight className="w-4 h-4" />
              </>
            )}
          </button>
          <button
            onClick={closeIfIdle}
            disabled={busy}
            data-testid="cross-create-skip"
            className="px-5 py-3 border border-zinc-300 hover:border-zinc-400 hover:bg-zinc-50 text-zinc-700 font-body rounded-md transition-colors disabled:opacity-50"
          >
            Più tardi
          </button>
        </div>
      </div>
    </div>
  );

  if (mode === "modal") {
    return (
      <div
        className="fixed inset-0 z-[100] flex items-center justify-center px-4 bg-black/60 backdrop-blur-sm"
        data-testid="cross-create-modal"
        onClick={(e) => { if (e.target === e.currentTarget) closeIfIdle(); }}
      >
        {body}
      </div>
    );
  }

  return <div data-testid="cross-create-card">{body}</div>;
}
